/**
 * Analytics Events Constants
 *
 * Centralized definitions for all analytics event names and parameters.
 * Based on patterns from myAir web and iOS applications.
 *
 * ## Usage Guidelines
 *
 * ### Recommended Approach (Use Dedicated Methods)
 * For common events, use the dedicated methods from useAnalytics hook:
 * ```tsx
 * import { useAnalytics } from 'services/analytics';
 *
 * const { logLogin, logStudyCreated, logError } = useAnalytics();
 *
 * // Recommended: Type-safe, documented methods
 * logLogin(LoginType.PASSWORD);
 * logStudyCreated(centerId);
 * logError('api_failure', error.message);
 * ```
 *
 * ### Advanced Approach (Use Generic logEvent with Constants)
 * For custom or one-off events, use logEvent with these constants:
 * ```tsx
 * import { useAnalytics } from 'services/analytics';
 * import { ANALYTICS_EVENTS, ANALYTICS_PARAMS } from 'services/analytics/events';
 *
 * const { logEvent } = useAnalytics();
 *
 * // For custom events not covered by dedicated methods
 * logEvent(ANALYTICS_EVENTS.FEATURE_USED, {
 *   [ANALYTICS_PARAMS.FEATURE]: 'advanced_filters',
 *   [ANALYTICS_PARAMS.SUCCESS]: true,
 * });
 * ```
 *
 * ### Screen Tracking (Automatic)
 * Use useScreenTracking in component files:
 * ```tsx
 * import { useScreenTracking } from 'services/analytics';
 *
 * function MyComponent() {
 *   // Automatically tracks screen view on mount
 *   useScreenTracking({ screenName: 'My Screen', screenClass: 'MyComponent' });
 *   return <div>Content</div>;
 * }
 * ```
 *
 * ## Benefits of Using Constants
 * - ✅ Type safety - catch typos at compile time
 * - ✅ Autocomplete - discover available events in IDE
 * - ✅ Single source of truth - one place to manage event names
 * - ✅ Easier refactoring - rename events globally
 * - ✅ Self-documenting - comments explain each event's purpose
 */

/**
 * Analytics event names.
 * Use these constants instead of string literals for consistency and type safety.
 */
export const ANALYTICS_EVENTS = {
  // ============================================================
  // Authentication Events
  // ============================================================
  LOGGED_IN: 'logged_in',
  /**
   * Base patterns for dimension-specific login tracking.
   * These generate dynamic event names like:
   * - login_by_region_us, login_by_region_eu, login_by_region_apac
   * - login_by_service_myair, login_by_service_airview, login_by_service_airsense
   * Generated automatically by logLogin() when region/service parameters are provided.
   */
  LOGIN_BY_REGION: 'login_by_region',
  LOGIN_BY_SERVICE: 'login_by_service',
  LOGGED_OUT: 'logged_out',
  // eslint-disable-next-line sonarjs/no-hardcoded-passwords
  FORGOT_PASSWORD: 'forgot_password',
  // eslint-disable-next-line sonarjs/no-hardcoded-passwords
  RESET_PASSWORD: 'reset_password',
  TWO_FACTOR_AUTH: 'two_factor_auth',
  SESSION_EXPIRED: 'session_expired',
  ACCOUNT_ACTIVATED: 'account_activated',
  ENROLLMENT_ACTIVATED: 'enrollment_activated',

  // ============================================================
  // Screen View Events
  // ============================================================
  SCREEN_VIEW_EVENT: 'screen_view_event', // Custom event to avoid Firebase strict typing

  // ============================================================
  // Study Events
  // ============================================================
  STUDY_CREATED: 'study_created',
  STUDY_VIEWED: 'study_viewed',
  STUDY_UPDATED: 'study_updated',
  STUDY_DELETED: 'study_deleted',
  STUDY_SETTINGS_UPDATED: 'study_settings_updated',
  STUDY_SEARCH: 'study_search',
  STUDY_FILTERED: 'study_filtered',
  STUDY_EXPORTED: 'study_exported',

  // ============================================================
  // Report Events
  // ============================================================
  REPORT_GENERATED: 'report_generated',
  REPORT_DOWNLOADED: 'report_downloaded',
  REPORT_PRINTED: 'report_printed',
  REPORT_SHARED: 'report_shared',
  REPORT_GENERATION_FAILED: 'report_generation_failed',

  // ============================================================
  // Center Events
  // ============================================================
  CENTER_CREATED: 'center_created',
  CENTER_VIEWED: 'center_viewed',
  CENTER_UPDATED: 'center_updated',
  CENTER_DELETED: 'center_deleted',
  CENTER_SETTINGS_UPDATED: 'center_settings_updated',
  CENTER_USER_ADDED: 'center_user_added',
  CENTER_USER_REMOVED: 'center_user_removed',
  CENTER_USER_ROLE_UPDATED: 'center_user_role_updated',
  CENTER_SWITCHED: 'center_switched',
  SNIPPET_CREATED: 'snippet_created',
  SNIPPET_UPDATED: 'snippet_updated',
  SNIPPET_DELETED: 'snippet_deleted',

  // ============================================================
  // Profile Events
  // ============================================================
  PROFILE_UPDATED: 'profile_updated',
  PROFILE_VIEWED: 'profile_viewed',
  // eslint-disable-next-line sonarjs/no-hardcoded-passwords
  PASSWORD_CHANGED: 'password_changed',
  SECURITY_SETTINGS_UPDATED: 'security_settings_updated',
  NOTIFICATION_SETTINGS_UPDATED: 'notification_settings_updated',
  LANGUAGE_CHANGED: 'language_changed',

  // ============================================================
  // Data Viewer Events
  // ============================================================
  DATA_VIEWER_ACCESSED: 'data_viewer_accessed',
  NIGHT_VIEWED: 'night_viewed',
  ALL_NIGHTS_VIEWED: 'all_nights_viewed',
  SURVEY_VIEWED: 'survey_viewed',
  INTERPRETATION_VIEWED: 'interpretation_viewed',
  INTERPRETATION_SAVED: 'interpretation_saved',
  INTERPRETATION_SIGNED: 'interpretation_signed',
  INTERPRETATION_RESET: 'interpretation_reset',
  EVENT_LOG_VIEWED: 'event_log_viewed',
  NOTES_ADDED: 'notes_added',
  NOTES_UPDATED: 'notes_updated',
  FILES_UPLOADED: 'files_uploaded',
  FILES_DOWNLOADED: 'files_downloaded',

  // ============================================================
  // Navigation Events
  // ============================================================
  NAVIGATION: 'navigation',
  SIDEBAR_TOGGLED: 'sidebar_toggled',
  TAB_CHANGED: 'tab_changed',
  MODAL_OPENED: 'modal_opened',
  MODAL_CLOSED: 'modal_closed',
  EXTERNAL_LINK_CLICKED: 'external_link_clicked',

  // ============================================================
  // API Client Events
  // ============================================================
  API_CLIENT_USED: 'api_client_used',
  API_REQUEST_SENT: 'api_request_sent',
  API_DOCUMENTATION_VIEWED: 'api_documentation_viewed',

  // ============================================================
  // Error Events
  // ============================================================
  ERROR: 'error',
  API_ERROR: 'api_error',
  VALIDATION_ERROR: 'validation_error',
  PERMISSION_DENIED: 'permission_denied',
  CENTER_2FA_UPDATE_FAILED: 'center_2fa_update_failed',
  // eslint-disable-next-line sonarjs/no-hardcoded-passwords
  PASSWORD_UPDATE_FAILED: 'password_update_failed',

  // ============================================================
  // Performance Events
  // ============================================================
  PAGE_LOAD_TIME: 'page_load_time',
  API_RESPONSE_TIME: 'api_response_time',

  // ============================================================
  // Feature Usage Events
  // ============================================================
  FEATURE_USED: 'feature_used',
  HELP_ACCESSED: 'help_accessed',
  SUPPORT_CONTACTED: 'support_contacted',
  TERMS_OF_USE_VIEWED: 'terms_of_use_viewed',
  PRIVACY_POLICY_VIEWED: 'privacy_policy_viewed',
} as const;

/**
 * Analytics parameter names.
 * Use these constants for parameter keys in event logging.
 */
export const ANALYTICS_PARAMS = {
  // Common parameters
  FEATURE: 'feature',
  SUCCESS: 'success',
  ERROR_TYPE: 'error_type',
  ERROR_MESSAGE: 'error_message',

  // Authentication parameters
  LOGIN_TYPE: 'login_type',
  LOGOUT_CAUSE: 'logout_cause',
  REGION: 'region',
  SERVICE: 'service',

  // Screen parameters
  SCREEN_NAME: 'screen_name',
  SCREEN_NAME_WEB: 'screen_name_web',
  SCREEN_CLASS: 'screen_class',
  PATH: 'path',

  // Study parameters
  STUDY_ID: 'study_id',
  CENTER_ID: 'center_id',
  NIGHT_NUMBER: 'night_number',
  REPORT_TYPE: 'report_type',

  // Center parameters
  USER_ROLE: 'user_role',
  USER_COUNT: 'user_count',

  // Navigation parameters
  DESTINATION: 'destination',
  SOURCE: 'source',

  // API parameters
  ENDPOINT: 'endpoint',
  METHOD: 'method',
  STATUS_CODE: 'status_code',
  RESPONSE_TIME: 'response_time',

  // Performance parameters
  LOAD_TIME: 'load_time',
  DURATION: 'duration',
} as const;

/**
 * Screen names for consistent tracking.
 */
export const SCREEN_NAMES = {
  // Auth screens
  LOGIN: 'screen_login',
  SERVICE_LOGIN: 'screen_service_login',
  SERVICE_SELECTION: 'screen_service_selection',
  REGION_SELECTION: 'screen_region_selection',
  TWO_FACTOR: 'screen_two_factor',
  // eslint-disable-next-line sonarjs/no-hardcoded-passwords
  FORGOT_PASSWORD: 'screen_forgot_password',
  // eslint-disable-next-line sonarjs/no-hardcoded-passwords
  RESET_PASSWORD: 'screen_reset_password',
  ACTIVATE_ENROLLMENT: 'screen_activate_enrollment',

  // Dashboard screens
  DASHBOARD: 'screen_dashboard',
  STUDIES_LIST: 'screen_studies_list',
  ALL_CENTERS: 'screen_all_centers',
  DEVELOPER_CENTER: 'screen_developer_center',

  // Profile screens
  PROFILE: 'screen_profile',
  PROFILE_CENTERS: 'screen_profile_centers',
  PROFILE_SECURITY: 'screen_profile_security',
  MY_PROFILE: 'screen_my_profile',

  // Study viewer screens
  STUDY_VIEWER: 'screen_study_viewer',
  STUDY_ALL_NIGHTS: 'screen_study_all_nights',
  STUDY_NIGHT_N: 'screen_study_night_n',
  STUDY_SURVEY: 'screen_study_survey',
  STUDY_INTERPRETATION: 'screen_study_interpretation',
  STUDY_EVENT_LOG: 'screen_study_event_log',
  STUDY_SETTINGS: 'screen_study_settings',
  STUDY_NOTES: 'screen_study_notes',
  STUDY_FILES: 'screen_study_files',

  // Center screens
  CENTER_MEMBERS: 'screen_center_members',
  CENTER_DETAILS: 'screen_center_details',
  CENTER_ENROLLMENTS: 'screen_center_enrollments',
  CENTER_SECURITY: 'screen_center_security',
  CENTER_IP_SNIPPETS: 'screen_center_ip_snippets',
  CENTER_CHANGE_LOG: 'screen_center_change_log',

  // Data viewer screens
  DATA_VIEWER: 'screen_data_viewer',

  // API Client screens
  API_CLIENT: 'screen_api_client',

  // Other screens
  TERMS_OF_USE: 'screen_terms_of_use',
  INSTRUCTION: 'screen_instruction',
} as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];
export type AnalyticsParamName = (typeof ANALYTICS_PARAMS)[keyof typeof ANALYTICS_PARAMS];
export type ScreenName = (typeof SCREEN_NAMES)[keyof typeof SCREEN_NAMES];

/**
 * Report type constants for download tracking.
 */
export const REPORT_TYPES = {
  SUMMARY_REPORT: 'summary_report',
  NIGHTLY_REPORT: 'nightly_report',
  EDF_FILE: 'edf_file',
  ERROR_REPORT: 'error_report',
} as const;

export type ReportType = (typeof REPORT_TYPES)[keyof typeof REPORT_TYPES];
